import { useState, useEffect, useRef, useCallback } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "@/utils/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Shield, Briefcase, LogOut, Clock, CheckCircle, Users, MessageSquare, Send, Paperclip, Image, File, Menu, X } from "lucide-react";


function MyCasesView() {
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({ total_assigned: 0, open_cases: 0, closed_cases: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyCases();
    fetchStats();
  }, []);

  const fetchMyCases = async () => {
    try {
      const response = await axios.get('/staff/my-cases');
      setCases(response.data);
    } catch (error) {
      toast.error("Failed to load cases");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/staff/dashboard-stats');
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats");
    }
  };

  if (loading) {
    return <div className="p-6">Loading your cases...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">My Assigned Cases</h2>
        <p className="text-slate-600">Manage your assigned worker cases</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-slate-600">Total Assigned</p>
            </div>
            <p className="text-3xl font-bold text-slate-900">{stats.total_assigned}</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-amber-600" />
              <p className="text-sm text-amber-600">Open Cases</p>
            </div>
            <p className="text-3xl font-bold text-amber-600">{stats.open_cases}</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <p className="text-sm text-emerald-600">Closed Cases</p>
            </div>
            <p className="text-3xl font-bold text-emerald-600">{stats.closed_cases}</p>
          </CardContent>
        </Card>
      </div>

      {/* Cases List */}
      <div className="space-y-4">
        {cases.map((c) => (
          <Card key={c.id} className="border-none shadow-sm" data-testid={`case-card-${c.id}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-slate-900 text-white font-semibold">
                      {c.member_name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-lg">{c.member_name}</h3>
                    <p className="text-sm text-slate-600">
                      Assigned on {new Date(c.assigned_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <Badge variant={c.case_status === "open" ? "default" : "outline"}>
                  {c.case_status === "open" ? "Open" : "Closed"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
        {cases.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No cases assigned yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Staff Chat View
function StaffChatView({ user }) {
  const [chatRooms, setChatRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [roomPresence, setRoomPresence] = useState({}); // room_id -> member presence
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then(permission => {
        setNotificationPermission(permission);
      });
    }
  }, []);

  const showNotification = (title, options) => {
    if ("Notification" in window && Notification.permission === "granted") {
      const notification = new Notification(title, options);
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  useEffect(() => {
    fetchChatRooms();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      fetchMessages(selectedRoom.id);
      fetchPresence(selectedRoom.id);
      connectWebSocket(selectedRoom.id);
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [selectedRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchChatRooms = async () => {
    try {
      const response = await axios.get('/chat/rooms');
      setChatRooms(response.data);
      if (response.data.length > 0) {
        setSelectedRoom(response.data[0]);
      }
    } catch (error) {
      console.error("Failed to load chat rooms");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (roomId) => {
    try {
      const response = await axios.get(`/chat/rooms/${roomId}/messages`);
      setMessages(response.data);
    } catch (error) {
      toast.error("Failed to load messages");
    }
  };

  const fetchPresence = async (roomId) => {
    try {
      const response = await axios.get(`/chat/rooms/${roomId}/presence`);
      // Staff sees member presence
      setRoomPresence(prev => ({
        ...prev,
        [roomId]: response.data.member
      }));
    } catch (error) {
      console.error("Failed to fetch presence");
    }
  };

  const connectWebSocket = useCallback((roomId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/chat/${roomId}?token=${token}`);

    ws.onopen = () => {
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'message') {
        setMessages(prev => [...prev, data]);
        
        // Send browser notification if message is from member and tab is not focused
        if (data.sender_id !== user.id && document.hidden) {
          // Find the room to get member name
          const room = chatRooms.find(r => r.id === roomId);
          showNotification(`New message from ${room?.member?.name || 'Worker'}`, {
            body: data.content?.substring(0, 100) || "You received a new message",
            icon: "/favicon.ico",
            tag: `message-${data.id}`
          });
        }
      } else if (data.type === 'presence') {
        // Update presence if it's the member (not staff)
        if (data.user_id !== user.id) {
          setRoomPresence(prev => ({
            ...prev,
            [roomId]: { user_id: data.user_id, status: data.status, last_seen: data.last_seen }
          }));
        }
      } else if (data.type === 'connected' && data.other_user_presence) {
        setRoomPresence(prev => ({
          ...prev,
          [roomId]: data.other_user_presence
        }));
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
    };

    wsRef.current = ws;
  }, [user.id]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedRoom) return;

    setSending(true);
    try {
      await axios.post(`/chat/rooms/${selectedRoom.id}/messages`, {
        content: newMessage.trim(),
        message_type: "text"
      });
      setNewMessage("");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedRoom) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await axios.post(`/chat/rooms/${selectedRoom.id}/upload`, {
            file_name: file.name,
            file_data: reader.result,
            file_type: file.type
          });
          toast.success("File sent successfully");
        } catch (error) {
          toast.error("Failed to upload file");
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Failed to read file");
      setUploading(false);
    }
    e.target.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderMessage = (msg) => {
    const isOwn = msg.sender_id === user.id;
    
    if (msg.message_type === 'image') {
      return (
        <div className={`max-w-[70%] rounded-2xl overflow-hidden ${
          isOwn ? 'bg-blue-600 rounded-br-md' : 'bg-white border border-slate-200 rounded-bl-md'
        }`}>
          <div className="p-2">
            <div className="rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
              <Image className="w-8 h-8 text-slate-400" />
            </div>
            <p className={`text-xs mt-2 ${isOwn ? 'text-blue-100' : 'text-slate-500'}`}>
              {msg.file_name}
            </p>
          </div>
          <div className={`px-3 py-2 ${isOwn ? '' : 'border-t'}`}>
            <p className={`text-xs ${isOwn ? 'text-blue-200' : 'text-slate-400'}`}>
              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
      );
    }
    
    if (msg.message_type === 'file') {
      return (
        <div className={`max-w-[70%] rounded-2xl px-4 py-3 ${
          isOwn ? 'bg-blue-600 text-white rounded-br-md' : 'bg-white border border-slate-200 text-slate-900 rounded-bl-md'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOwn ? 'bg-blue-500' : 'bg-slate-100'}`}>
              <File className={`w-5 h-5 ${isOwn ? 'text-white' : 'text-slate-500'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{msg.file_name}</p>
              <p className={`text-xs ${isOwn ? 'text-blue-200' : 'text-slate-500'}`}>
                {formatFileSize(msg.file_size)}
              </p>
            </div>
          </div>
          <p className={`text-xs mt-2 ${isOwn ? 'text-blue-200' : 'text-slate-400'}`}>
            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      );
    }
    
    return (
      <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${
        isOwn ? 'bg-blue-600 text-white rounded-br-md' : 'bg-white border border-slate-200 text-slate-900 rounded-bl-md'
      }`}>
        <p className="text-sm">{msg.content}</p>
        <p className={`text-xs mt-1 ${isOwn ? 'text-blue-200' : 'text-slate-400'}`}>
          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    );
  };

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return "Never";
    const date = new Date(lastSeen);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="p-6">Loading chat...</div>;
  }

  if (chatRooms.length === 0) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-slate-900">Messages</h2>
        <div className="text-center py-12 text-slate-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No active chats available</p>
          <p className="text-sm mt-2">You will see chats here once you have assigned workers.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="staff-chat-view">
      <h2 className="text-2xl font-bold text-slate-900">Messages</h2>
      
      <Card className="border-none shadow-sm overflow-hidden">
        <div className="flex h-[500px]">
          {/* Chat Rooms List */}
          <div className="w-72 border-r bg-slate-50 overflow-y-auto">
            <div className="p-3 border-b bg-white">
              <h3 className="font-semibold text-sm text-slate-700">Workers</h3>
            </div>
            {chatRooms.map((room) => {
              const presence = roomPresence[room.id] || { status: "offline" };
              return (
              <div
                key={room.id}
                onClick={() => setSelectedRoom(room)}
                className={`p-3 cursor-pointer border-b transition ${
                  selectedRoom?.id === room.id
                    ? 'bg-white border-l-2 border-l-slate-900'
                    : 'hover:bg-white'
                }`}
                data-testid={`chat-room-${room.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-slate-200 text-slate-700 font-semibold text-sm">
                        {room.member?.name?.split(" ").map(n => n[0]).join("").substring(0, 2) || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <span 
                      className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-slate-50 ${
                        presence.status === "online" ? "bg-emerald-500" : "bg-slate-400"
                      }`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm text-slate-900 truncate">{room.member?.name}</p>
                      {room.unread_count > 0 && (
                        <Badge variant="destructive" className="h-5 min-w-[20px] text-xs">
                          {room.unread_count}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">
                      {room.last_message?.content || "No messages yet"}
                    </p>
                  </div>
                </div>
              </div>
            )})}
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col">
            {selectedRoom ? (
              <>
                {/* Chat Header */}
                <div className="bg-slate-50 border-b px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-emerald-600 text-white font-semibold">
                          {selectedRoom.member?.name?.split(" ").map(n => n[0]).join("").substring(0, 2) || "?"}
                        </AvatarFallback>
                      </Avatar>
                      {/* Presence Indicator */}
                      <span 
                        className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-slate-50 ${
                          (roomPresence[selectedRoom.id]?.status === "online") ? "bg-emerald-500" : "bg-slate-400"
                        }`}
                        data-testid="staff-presence-indicator"
                      />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{selectedRoom.member?.name}</p>
                      <p className="text-xs text-slate-500">
                        {roomPresence[selectedRoom.id]?.status === "online" 
                          ? <span className="text-emerald-600 font-medium">Online</span>
                          : `Last seen ${formatLastSeen(roomPresence[selectedRoom.id]?.last_seen)}`
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50" data-testid="staff-messages-container">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                      data-testid={`staff-message-${msg.id}`}
                    >
                      {renderMessage(msg)}
                    </div>
                  ))}
                  {messages.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={handleSendMessage} className="border-t bg-white p-4">
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      data-testid="staff-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || !selectedRoom}
                      data-testid="staff-attach-file-button"
                    >
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={uploading ? "Uploading file..." : "Type your message..."}
                      className="flex-1"
                      disabled={sending || uploading}
                      data-testid="staff-message-input"
                    />
                    <Button type="submit" disabled={sending || uploading || !newMessage.trim()} data-testid="staff-send-message-button">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <p>Select a worker to start chatting</p>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function StaffDashboard({ user, onLogout }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: "/staff", label: "My Cases", icon: Briefcase, testId: "nav-staff-cases", exact: true },
    { path: "/staff/chat", label: "Messages", icon: MessageSquare, testId: "nav-staff-chat" },
  ];

  const isActiveRoute = (path, exact = false) => {
    if (exact) {
      return location.pathname === path || location.pathname.includes("/cases");
    }
    return location.pathname.includes(path.split("/").pop());
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/staff" className="flex items-center gap-3">
              <div className="bg-slate-900 w-10 h-10 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-slate-900">MyShield Staff</h1>
                <p className="text-xs text-slate-600">Worker Management</p>
              </div>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-slate-900">{user.full_name}</p>
                <Badge variant="secondary" className="text-xs mt-1">Staff</Badge>
              </div>
              <Button variant="outline" size="sm" onClick={onLogout} data-testid="staff-logout-button" className="hidden sm:flex">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="staff-mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Navigation */}
      <nav className="bg-white border-b border-slate-200 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 py-4 px-2 border-b-2 font-medium text-sm transition ${
                  isActiveRoute(item.path, item.exact)
                    ? "border-slate-900 text-slate-900"
                    : "border-transparent text-slate-600 hover:text-slate-900"
                }`}
                data-testid={item.testId}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[73px] z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl animate-slide-in">
            <div className="p-4 border-b">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-blue-600 text-white font-semibold">
                    {user.full_name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-slate-900">{user.full_name}</p>
                  <Badge variant="secondary" className="text-xs">Staff</Badge>
                </div>
              </div>
            </div>
            <div className="p-4 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition ${
                    isActiveRoute(item.path, item.exact)
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                  data-testid={`mobile-${item.testId}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
              <Button variant="outline" className="w-full" onClick={onLogout} data-testid="staff-mobile-logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu Animation Styles */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div>
          <Routes>
            <Route path="/" element={<MyCasesView />} />
            <Route path="/cases" element={<MyCasesView />} />
            <Route path="/chat" element={<StaffChatView user={user} />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
